import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ActivitySettings from "./ActivitySettings";
import Toster from "../../common/Toster";
import { listActivities } from "./service";
import AdminActivityWizard from "./adminWizard/AddActivity";
import Pagination from "../../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  TableActionButton,
  TableActionsCell,
} from "../../common/adminTable";
import { buildPaginationMeta } from "../../../utils/clientPagination";
import { useCloseOnSidebarNavigate } from "../../../hooks/useCloseOnSidebarNavigate";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import useTableSearch from "../../../hooks/useTableSearch";

const FILTER_SELECT_CLASS =
  "min-w-[8.5rem] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-gray-400 focus:outline-none";

const SupplierActivities = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [submissionStatus, setSubmissionStatus] = useState("");
  const [approvalStatus, setApprovalStatus] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardId, setWizardId] = useState(null);
  const [toast, setToast] = useState(null);
  const location = useLocation();

  const closeWizard = useCallback(() => {
    setWizardOpen(false);
    setWizardId(null);
  }, []);

  useEffect(() => {
    closeWizard();
  }, [location.pathname, closeWizard]);

  useCloseOnSidebarNavigate(closeWizard);

  const filters = useMemo(
    () => ({
      submission_status: submissionStatus,
      is_approved: approvalStatus,
      per_page: 10,
    }),
    [submissionStatus, approvalStatus]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["supplierActivities", currentPage, activeSearch, filters],
    queryFn: () => listActivities(currentPage, activeSearch, filters),
    keepPreviousData: true,
  });

  const activities = data?.data || [];
  const current_page = data?.current_page || 1;
  const last_page = data?.last_page || 1;
  const total = data?.total || 0;
  const per_page = data?.per_page || 10;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  const clearFilters = useCallback(() => {
    clearSearch();
    setSubmissionStatus("");
    setApprovalStatus("");
    setCurrentPage(1);
  }, [clearSearch]);

  const openSettings = (activity) => {
    setSelected(activity);
    setSettingsOpen(true);
  };

  const openEditWizard = (activityId) => {
    setWizardId(activityId);
    setWizardOpen(true);
  };

  if (wizardOpen && wizardId) {
    return (
      <AdminActivityWizard
        open={wizardOpen}
        activityId={wizardId}
        onClose={() => {
          setWizardOpen(false);
          setWizardId(null);
        }}
      />
    );
  }

  const isFlagOn = (v) => v === 1 || v === true || v === "1";

  const approvalCode = (v) => {
    if (v === true || v === 1 || v === "1") return 1;
    if (v === 2 || v === "2") return 2;
    return 0;
  };

  const renderStatus = (a) => {
    if (approvalCode(a.is_approved) === 1) {
      return <span className="text-sm text-green-700">Approved</span>;
    }
    if (approvalCode(a.is_approved) === 2) {
      return <span className="text-sm text-red-600">Rejected</span>;
    }
    if (a.submitted_at) {
      return <span className="text-sm text-amber-700">Pending</span>;
    }
    return <span className="text-sm text-gray-500">Draft</span>;
  };

  const hasActiveFilters = activeSearch || submissionStatus || approvalStatus;

  const pageMeta = ADMIN_PAGE_META.supplierActivities;

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin h-8 w-8 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-primary font-medium">Loading activities...</span>
            </div>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="fi fi-rr-exclamation text-red-500 text-4xl mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading activities</h3>
              <p className="text-gray-600">{error.message}</p>
            </div>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout>
      <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />

      <AdminPageCard>
        <AdminPageCardToolbar className="border-b border-gray-100 bg-white px-4 py-3 sm:px-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <TableSearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search activities..."
                className="w-full sm:max-w-[220px]"
              />
              <select
                value={submissionStatus}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSubmissionStatus(e.target.value);
                }}
                className={FILTER_SELECT_CLASS}
                aria-label="Submission status"
              >
                <option value="">All submitted</option>
                <option value="pending">Pending approval</option>
              </select>
              <select
                value={approvalStatus}
                onChange={(e) => {
                  setCurrentPage(1);
                  setApprovalStatus(e.target.value);
                }}
                className={FILTER_SELECT_CLASS}
                aria-label="Approval status"
              >
                <option value="">All approvals</option>
                <option value="0">Pending</option>
                <option value="1">Approved</option>
                <option value="2">Rejected</option>
              </select>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>
                <span className="font-semibold text-gray-900">{total}</span>{" "}
                {total === 1 ? "activity" : "activities"}
              </span>
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="font-medium text-gray-600 transition hover:text-gray-900"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination pagination={pagination} onPageChange={handlePageChange} />
            </AdminTableFooter>
          }
        >
        <AdminTableHead>
          <AdminTableTh>Activity</AdminTableTh>
          <AdminTableTh>Supplier</AdminTableTh>
          <AdminTableTh>Category</AdminTableTh>
          <AdminTableTh>Location</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh>Submitted</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {activities.length === 0 ? (
            <TableEmptyRow
              colSpan={7}
              icon="fi fi-rr-activity"
              emptyMessage="No activities found"
              searchQuery={activeSearch || submissionStatus || approvalStatus}
              onClearSearch={clearFilters}
            />
          ) : (
            activities.map((a) => (
              <AdminTableRow key={a.id}>
                <AdminTableCell className="font-medium text-gray-900">
                  <div>
                    <p>{a.name || "Untitled"}</p>
                    {(isFlagOn(a.popular) || isFlagOn(a.recommended)) && (
                      <p className="mt-0.5 text-xs text-gray-500">
                        {[
                          isFlagOn(a.popular) ? "Popular" : null,
                          isFlagOn(a.recommended) ? "Recommended" : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                  </div>
                </AdminTableCell>
                <AdminTableCell>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{a.user?.name || "—"}</p>
                    <p className="text-xs text-gray-500">{a.user?.phone || ""}</p>
                  </div>
                </AdminTableCell>
                <AdminTableCell className="text-sm text-gray-600">
                  {a.activity_category?.name || "N/A"}
                </AdminTableCell>
                <AdminTableCell className="text-sm text-gray-600">
                  {a.location || a.address || "N/A"}
                </AdminTableCell>
                <AdminTableCell>{renderStatus(a)}</AdminTableCell>
                <AdminTableCell nowrap className="text-sm text-gray-500">
                  {formatListingDateTime(a.submitted_at)}
                </AdminTableCell>
                <TableActionsCell>
                  <TableActionButton
                    variant="edit"
                    showLabel={false}
                    ariaLabel="Update details"
                    onClick={() => openEditWizard(a.id)}
                  />
                  <TableActionButton
                    variant="settings"
                    showLabel={false}
                    ariaLabel="Activity status"
                    onClick={() => openSettings(a)}
                  />
                </TableActionsCell>
              </AdminTableRow>
            ))
          )}
        </AdminTableBody>
      </AdminTable>
      </AdminPageCard>

      {settingsOpen && selected && (
        <ActivitySettings
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          activityData={selected}
          onNotify={setToast}
        />
      )}

      {toast && (
        <Toster
          message={toast.message}
          type={toast.type}
          duration={3000}
          onClose={() => setToast(null)}
        />
      )}
    </AdminPageLayout>
  );
};

export default SupplierActivities;
